import { APP_DIAGNOSTICS } from "../config/global.configuration";
import type { AppMemoryCategoryTotals, AppMemoryReport, MapStopsMemoryStats } from "./AppMemoryReport.types";
import {
    MEMORY_DIAGNOSTICS_LOG_PREFIX,
    memoryDiagnosticsDebug,
    memoryDiagnosticsGroupCollapsed,
    memoryDiagnosticsGroupEnd,
    memoryDiagnosticsInfo,
    memoryDiagnosticsTable,
    memoryDiagnosticsWarn,
} from "./MemoryDiagnosticsLog.tools";
import { formatBytes } from "./MemoryEstimate.tools";

/** One row in the level-2 category table. */
export interface MemoryCategoryRow {
    "category":  string;
    "sizeBytes": number;
    "size":      string;
    "percent":   string;
    "delta":     string;
    "notes":     string;
}

/** Output depth for {@link logFormattedMemoryReport}. */
export type MemoryLogDepth = "light" | "standard" | "full";

/**
 * Formats a local time stamp for log headers.
 *
 * @param date Clock source.
 * @returns HH:mm:ss string.
 */
export function formatMemoryLogTimestamp( date: Date = new Date() ): string {
    return date.toLocaleTimeString( undefined, {
        "hour":   "2-digit",
        "minute": "2-digit",
        "second": "2-digit",
        "hour12": false,
    } );
}

/**
 * Formats a signed byte delta for display.
 *
 * @param deltaBytes Signed change since previous snapshot.
 * @returns Human-readable delta with direction marker.
 */
export function formatMemoryDelta( deltaBytes: number | undefined ): string {
    if ( deltaBytes === undefined ) {
        return "—";
    }

    const threshold = APP_DIAGNOSTICS.memoryDeltaMinBytes;
    if ( Math.abs( deltaBytes ) < threshold ) {
        return "— stable";
    }

    const sign = deltaBytes > 0 ? "+" : "";
    const marker = deltaBytes > 0 ? " ▲" : " ▼";

    return `${ sign }${ formatBytes( Math.abs( deltaBytes ) ) }${ marker }`;
}

/**
 * Builds category rows for the level-2 table: heap reference, remainder, breakdown, tracked total.
 *
 * @param totals Current category totals.
 * @param previous Previous totals for delta, if any.
 * @returns Rows in display order (summary headers, sorted breakdown, footer).
 */
/**
 * Builds a short notes string for the map stops category row.
 *
 * @param mapStops Latest preload stats, if recorded.
 * @returns Human-readable feature breakdown.
 */
const formatMapStopsCategoryNotes = ( mapStops: MapStopsMemoryStats | null | undefined ): string => {
    if ( mapStops === null || mapStops === undefined ) {
        return "no successful preload fetch yet";
    }

    return `${ mapStops.parentFeatures } parent · ${ mapStops.childFeatures } child features`;
};

export function buildMemoryCategoryRows(
    totals: AppMemoryCategoryTotals,
    previous: AppMemoryCategoryTotals | null,
    mapStops?: MapStopsMemoryStats | null,
): MemoryCategoryRow[] {
    const tracked = totals.trackedTotalBytes;
    const heapUsed = totals.jsHeapUsedBytes;
    const unaccountedBytes = totals.unaccountedHeapBytes ?? 0;

    const deltaFor = (
        current: number,
        key: keyof AppMemoryCategoryTotals | "unaccounted" | "mapLibreTracked" | "jsHeapUsed",
    ): string => {
        if ( previous === null ) {
            return "—";
        }

        const previousValue = key === "unaccounted"
            ?  previous.unaccountedHeapBytes ?? 0
            : key === "mapLibreTracked"
                ? previous.mapLibreTrackedBytes
                : key === "jsHeapUsed"
                    ?  previous.jsHeapUsedBytes ?? 0
                    : key === "trackedTotalBytes"
                        ? previous.trackedTotalBytes
                        : previous[key];

        return formatMemoryDelta( current - ( previousValue ?? 0 ) );
    };

    const toRow = ( entry: {
        "label":         string;
        "bytes":         number;
        "percent":       string;
        "notes":         string;
        "deltaKey":      keyof AppMemoryCategoryTotals | "unaccounted" | "mapLibreTracked" | "jsHeapUsed";
        "sizeOverride"?: string;
    } ): MemoryCategoryRow => ( {
        "category":  entry.label,
        "sizeBytes": entry.bytes,
        "size":      entry.sizeOverride ?? formatBytes( entry.bytes ),
        "percent":   entry.percent,
        "delta":     deltaFor( entry.bytes, entry.deltaKey ),
        "notes":     entry.notes,
    } );

    const summaryRows: MemoryCategoryRow[] = [];

    if ( heapUsed !== undefined && heapUsed > 0 ) {
        summaryRows.push( toRow( {
            "label":    "— js heap used —",
            "bytes":    heapUsed,
            "percent":  "100% reference",
            "notes":    "Chromium performance.memory (whole tab)",
            "deltaKey": "jsHeapUsed",
        } ) );
    }

    summaryRows.push( toRow( {
        "label":   "not determined (heap − tracked)",
        "bytes":   unaccountedBytes,
        "percent": heapUsed !== undefined && heapUsed > 0 && totals.unaccountedHeapBytes !== undefined
            ? `${ ( unaccountedBytes / heapUsed * 100 ).toFixed( 0 ) }% of heap`
            : "n/a",
        "notes": heapUsed !== undefined
            ? "MapLibre tiles, GPU, devtools/HMR — see map-idle Δ"
            : "requires Chromium heap (heap used − tracked counters)",
        "deltaKey":     "unaccounted",
        "sizeOverride": heapUsed !== undefined || unaccountedBytes > 0
            ? undefined
            : "n/a",
    } ) );

    const trackedBreakdown: Array<{
        "key":   keyof AppMemoryCategoryTotals | "mapLibreTracked";
        "label": string;
        "bytes": number;
        "notes": string;
    }> = [
        {
            "key":   "mapStopsBytes",
            "label": "map stops",
            "bytes": totals.mapStopsBytes,
            "notes": formatMapStopsCategoryNotes( mapStops ),
        },
        {
            "key":   "mapOverlayBytes",
            "label": "map overlays",
            "bytes": totals.mapOverlayBytes,
            "notes": "itinerary layers + drawn geometry (excludes stops)",
        },
        {
            "key":   "realtimeBytes",
            "label": "realtime",
            "bytes": totals.realtimeBytes,
            "notes": "",
        },
        {
            "key":   "mapLibreTracked",
            "label": "maplibre style+geojson",
            "bytes": totals.mapLibreTrackedBytes,
            "notes": "tile caches NOT in this row",
        },
        {
            "key":   "ephemeralStoreBytes",
            "label": "ephemeral store",
            "bytes": totals.ephemeralStoreBytes,
            "notes": "",
        },
        {
            "key":   "persistedStoreBytes",
            "label": "persisted store",
            "bytes": totals.persistedStoreBytes,
            "notes": "",
        },
        {
            "key":   "vehicleLayerBytes",
            "label": "WebGL vehicles",
            "bytes": totals.vehicleLayerBytes,
            "notes": "",
        },
        {
            "key":   "tripBuffersBytes",
            "label": "trip record / replay",
            "bytes": totals.tripBuffersBytes,
            "notes": "",
        },
        {
            "key":   "indexedDbCacheBytes",
            "label": "indexedDB axios cache",
            "bytes": totals.indexedDbCacheBytes,
            "notes": "",
        },
    ];

    const breakdownRows = trackedBreakdown.
        map( ( entry ) => toRow( {
            "label":    entry.label,
            "bytes":    entry.bytes,
            "percent":  tracked > 0 ? `${ ( entry.bytes / tracked * 100 ).toFixed( 0 ) }% of tracked` : "0%",
            "notes":    entry.notes,
            "deltaKey": entry.key,
        } ) ).
        sort( ( left, right ) => right.sizeBytes - left.sizeBytes );

    const footerRow = toRow( {
        "label":   "— tracked total (measured) —",
        "bytes":   tracked,
        "percent": heapUsed !== undefined && heapUsed > 0
            ? `${ ( tracked / heapUsed * 100 ).toFixed( 0 ) }% of heap`
            : "sum of rows below",
        "notes":    "sum of FlowRide counters (excludes not determined)",
        "deltaKey": "trackedTotalBytes",
    } );

    return [
        ...summaryRows,
        ...breakdownRows,
        footerRow,
    ];
}

/**
 * Picks the top category deltas for the verdict line.
 *
 * @param rows Category table rows.
 * @param limit Maximum entries.
 * @returns TOP Δ summary fragment.
 */
export function buildTopDeltaSummary( rows: MemoryCategoryRow[], limit = 3 ): string {
    const movers = rows.
        filter( ( row ) => row.delta !== "—" && row.delta !== "— stable" ).
        sort( ( left, right ) => {
            const leftMatch = ( /([+-]?[\d.]+)\s*(B|KiB|MiB|GiB)/ ).exec( left.delta );
            const rightMatch = ( /([+-]?[\d.]+)\s*(B|KiB|MiB|GiB)/ ).exec( right.delta );
            const leftValue = leftMatch !== null ? Number.parseFloat( leftMatch[1] ) : 0;
            const rightValue = rightMatch !== null ? Number.parseFloat( rightMatch[1] ) : 0;

            return rightValue - leftValue;
        } ).
        slice( 0, limit );

    if ( movers.length === 0 ) {
        return "TOP Δ: — stable";
    }

    return `TOP Δ: ${ movers.map( ( row ) => `${ row.category } ${ row.delta }` ).join( " | " ) }`;
}

/**
 * Computes signed deltas between two category total snapshots.
 *
 * @param current Current totals.
 * @param previous Previous totals.
 * @returns Heap used delta in bytes when both sides are defined.
 */
export function computeHeapUsedDelta(
    current: AppMemoryCategoryTotals,
    previous: AppMemoryCategoryTotals | null,
): number | undefined {
    if ( previous === null ||
      current.jsHeapUsedBytes === undefined ||
      previous.jsHeapUsedBytes === undefined ) {
        return undefined;
    }

    return current.jsHeapUsedBytes - previous.jsHeapUsedBytes;
}

/**
 * Formats heap used for the level-1 verdict line.
 *
 * @param totals Current category totals.
 * @returns Human-readable heap size or browser hint.
 */
export function formatVerdictHeapSummary( totals: AppMemoryCategoryTotals ): string {
    if ( totals.jsHeapUsedBytes !== undefined ) {
        return formatBytes( totals.jsHeapUsedBytes );
    }

    return "n/a (Chromium performance.memory only)";
}

/**
 * Formats tracked footprint for the level-1 verdict line.
 *
 * @param totals Current category totals.
 * @returns Percent of heap when available, otherwise absolute tracked bytes.
 */
export function formatVerdictTrackedSummary( totals: AppMemoryCategoryTotals ): string {
    if ( totals.jsHeapUsedBytes !== undefined && totals.jsHeapUsedBytes > 0 ) {
        return `${ ( totals.trackedTotalBytes / totals.jsHeapUsedBytes * 100 ).toFixed( 0 ) }%`;
    }

    return formatBytes( totals.trackedTotalBytes );
}

/**
 * Formats unaccounted heap for the level-1 verdict line.
 *
 * @param totals Current category totals.
 * @returns Percent of heap when available, otherwise absolute bytes or hint.
 */
export function formatVerdictUnaccountedSummary( totals: AppMemoryCategoryTotals ): string {
    if ( totals.jsHeapUsedBytes !== undefined && totals.jsHeapUsedBytes > 0 &&
      totals.unaccountedHeapBytes !== undefined ) {
        return `${ ( totals.unaccountedHeapBytes / totals.jsHeapUsedBytes * 100 ).toFixed( 0 ) }%`;
    }

    if ( totals.unaccountedHeapBytes !== undefined ) {
        return formatBytes( totals.unaccountedHeapBytes );
    }

    return "n/a (needs Chromium heap)";
}

/**
 * Logs a memory report using the 3-level readability contract.
 *
 * @param params Report, depth, and optional previous snapshot.
 * @returns Nothing.
 */
export function logFormattedMemoryReport( params: {
    "report":           AppMemoryReport;
    "triggerContext":   string;
    "depth":            MemoryLogDepth;
    "previousTotals":   AppMemoryCategoryTotals | null;
    "throttledDetail"?: boolean;
} ): void {
    const { report, triggerContext, depth, previousTotals, throttledDetail = false } = params;
    const totals = report.categoryTotals;
    const timestamp = formatMemoryLogTimestamp();
    const heapUsed = formatVerdictHeapSummary( totals );
    const heapDelta = formatMemoryDelta( computeHeapUsedDelta( totals, previousTotals ) );
    const trackedSummary = formatVerdictTrackedSummary( totals );
    const unaccountedSummary = formatVerdictUnaccountedSummary( totals );

    const categoryRows = buildMemoryCategoryRows( totals, previousTotals, report.mapStops );
    const topDelta = buildTopDeltaSummary( categoryRows );

    const heapWarn = computeHeapUsedDelta( totals, previousTotals ) !== undefined &&
      Math.abs( computeHeapUsedDelta( totals, previousTotals ) ?? 0 ) > 10 * 1024 * 1024;
    const unaccountedWarn = triggerContext === "map-idle" &&
      previousTotals !== null &&
      totals.unaccountedHeapBytes !== undefined &&
      previousTotals.unaccountedHeapBytes !== undefined &&
      totals.unaccountedHeapBytes - previousTotals.unaccountedHeapBytes > 5 * 1024 * 1024;

    if ( heapWarn || unaccountedWarn ) {
        memoryDiagnosticsWarn( `${ MEMORY_DIAGNOSTICS_LOG_PREFIX } WARN ${ timestamp } | TRIGGER ${ triggerContext } | ` +
          ( unaccountedWarn
              ? "UNACCOUNTED grew > 5 MiB after map-idle — likely MapLibre tile cache (confirm with heap snapshot)"
              : "Heap moved > 10 MiB since last snapshot" ) );
    }

    memoryDiagnosticsInfo( `${ MEMORY_DIAGNOSTICS_LOG_PREFIX } ${ timestamp } | TRIGGER ${ triggerContext } | ` +
      `heap ${ heapUsed } used (${ heapDelta }) | tracked ${ trackedSummary } · unaccounted ${ unaccountedSummary }` +
      ( throttledDetail
          ? ` (detail skipped — throttled ${ APP_DIAGNOSTICS.memoryProfilingMinIntervalMs / 1000 }s; use __flowrideAppMemory() for full dump)`
          : "" ) );
    memoryDiagnosticsInfo( `${ MEMORY_DIAGNOSTICS_LOG_PREFIX }   ${ topDelta }` );

    if ( depth === "light" ) {
        return;
    }

    const tableRows = categoryRows.map( ( row ) => ( {
        "category":       row.category,
        "size":           row.size,
        "% heap/tracked": row.percent,
        "Δ since last":   row.delta,
        "notes":          row.notes,
    } ) );
    memoryDiagnosticsTable(
        `${ MEMORY_DIAGNOSTICS_LOG_PREFIX } category breakdown — ${ triggerContext }`,
        tableRows,
    );

    if ( depth === "standard" ) {
        return;
    }

    memoryDiagnosticsGroupCollapsed( `${ MEMORY_DIAGNOSTICS_LOG_PREFIX } detail — ${ triggerContext }` );

    if ( report.realtime !== null ) {
        const { cache, vehicles } = report.realtime;
        memoryDiagnosticsInfo( `▸ Realtime (${ formatBytes( cache.totalBytes ) }) — ` +
          `shapes ${ cache.shapesCount } (${ cache.shapePointsTotal } pts), ` +
          `timelines ${ cache.timelinesCount } (${ cache.timelinePointsTotal } pts)` );
        if ( vehicles !== undefined ) {
            memoryDiagnosticsInfo( `  vehicles metadata ${ formatBytes( vehicles.vehicleMetadataBytes ) } · ` +
              `color/type caches ${ formatBytes( vehicles.colorCacheBytes + vehicles.typeCacheBytes ) }` );
        }
    }

    if ( report.mapStops !== null ) {
        const stops = report.mapStops;
        memoryDiagnosticsInfo( `▸ Map stops (${ formatBytes( stops.estimatedBytes ) }) — ` +
          `${ stops.parentFeatures } parent · ${ stops.childFeatures } child features · ` +
          `${ stops.stopsFetched } stops in last fetch` );
    }

    const mapLibre = report.mapLibreHints;
    memoryDiagnosticsInfo( `▸ MapLibre (${ formatBytes( mapLibre.mapLibreTrackedBytes ) } tracked — tile cache in UNACCOUNTED) — ` +
      `${ mapLibre.vectorOrRasterSourceCount } vector/raster · ${ mapLibre.layerCount } layers · ` +
      `geojson inline ${ formatBytes( mapLibre.geojsonPayloadSerializedBytes ) }` );
    if ( mapLibre.topGeojsonSources.length > 0 ) {
        for ( const source of mapLibre.topGeojsonSources ) {
            memoryDiagnosticsInfo( `    ↳ ${ source.sourceId }: ${ formatBytes( source.estimatedBytes ) }` );
        }
    }

    memoryDiagnosticsInfo( `▸ Stores (${ formatBytes( totals.ephemeralStoreBytes + totals.persistedStoreBytes ) }) · ` +
      `WebGL (${ formatBytes( totals.vehicleLayerBytes ) }) · ` +
      `Navigation (${ formatBytes( totals.tripBuffersBytes ) }) · ` +
      `IndexedDB (${ formatBytes( totals.indexedDbCacheBytes ) })` );

    memoryDiagnosticsInfo( "Object-level breakdown: Chrome DevTools → Memory → Heap snapshot" );

    if ( import.meta.env.DEV ) {
        memoryDiagnosticsInfo( "DEV NOTE: Vite HMR, source maps and React DevTools inflate heap in dev." );
    }

    memoryDiagnosticsDebug( `${ MEMORY_DIAGNOSTICS_LOG_PREFIX } raw report`, report );
    memoryDiagnosticsGroupEnd();
}

/**
 * Resolves console log depth from throttle state and context.
 *
 * @param params Context and throttle flags.
 * @returns light, standard, or full depth.
 */
export function resolveMemoryLogDepth( params: {
    "context":        string;
    "skipThrottle"?:  boolean;
    "throttledLight": boolean;
    "forceFull"?:     boolean;
} ): MemoryLogDepth {
    if ( params.throttledLight ) {
        return "light";
    }

    if ( params.skipThrottle === true || params.context === "manual" || params.forceFull === true ) {
        return "full";
    }

    if ( params.context === "periodic" ) {
        return "full";
    }

    return "standard";
}

/** Exported for unit tests. */
export const __memoryLogFormatterTestOnly = {
    buildMemoryCategoryRows,
    buildTopDeltaSummary,
    formatVerdictHeapSummary,
    formatVerdictTrackedSummary,
    formatVerdictUnaccountedSummary,
};
